import {
  UploadOutlined,
  UserOutlined,
  BellOutlined,
  PhoneOutlined,
  MailOutlined,
  LockOutlined,
  TeamOutlined,
  MobileOutlined,
  SaveOutlined,
} from "@ant-design/icons"
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  message,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
  Upload,
} from "antd"
import {
  getAuth,
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth"
import { getDownloadURL, ref, uploadBytes } from "firebase/storage"
import React, { useEffect, useState } from "react"
import { db, storage } from "../../../firebase/config"
import { useAuthContext } from "../../../hooks/useAuthContext"
import { doc, updateDoc, getDoc } from "firebase/firestore"

const { Text, Title } = Typography

export default function EditUserProfile() {
  const { user } = useAuthContext()
  const [loading, setLoading] = useState(false)

  // Profile fields
  const [imageUrl, setImageUrl] = useState(null)
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [slackEmail, setSlackEmail] = useState("")

  // Notification preferences
  const [notifications, setNotifications] = useState(false)
  const [notificationTime, setNotificationTime] = useState("")
  const [sendDailyOrderRecap, setSendDailyOrderRecap] = useState(false)
  const [sendOrderCompleted, setSendOrderCompleted] = useState(false)
  const [sendOrderDoneByDesigner, setSendOrderDoneByDesigner] = useState(false)

  // Roles
  const [roles, setRoles] = useState({})

  // Mobile app
  const [hasAppLogin, setHasAppLogin] = useState(false)
  const [mobileAppLastLogin, setMobileAppLastLogin] = useState("")
  const [expoPushToken, setExpoPushToken] = useState("")

  // Password change
  const [oldPassword, setOldPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmNewPassword, setConfirmNewPassword] = useState("")

  const auth = getAuth()

  // Fetch user data from Firestore on mount
  useEffect(() => {
    if (!user) return

    const fetchUserData = async () => {
      setLoading(true)
      try {
        const userDocRef = doc(db, "users", user.uid)
        const userDocSnap = await getDoc(userDocRef)

        if (userDocSnap.exists()) {
          const data = userDocSnap.data()

          // Set all form fields
          setImageUrl(user?.photoURL || data.imageUrl || "")
          setName(data.name || user?.displayName || "")
          setEmail(user?.email || "")
          setPhoneNumber(data.phoneNumber || "")
          setSlackEmail(data.slackEmail || "")
          setNotifications(data.notifications || false)
          setNotificationTime(data.notificationTime || "")
          setSendDailyOrderRecap(data.sendDailyOrderRecapMobileNotification || false)
          setSendOrderCompleted(data.sendOrderCompletedMobileNotification || false)
          setSendOrderDoneByDesigner(data.sendOrderDoneByDesignerMobileNotification || false)
          setRoles(data.roles || {})
          setHasAppLogin(data.hasAppLogin || false)
          setMobileAppLastLogin(
            data.mobileAppLastLogin
              ? new Date(data.mobileAppLastLogin.toDate()).toLocaleString()
              : "",
          )
          setExpoPushToken(data.expoPushToken || "")
        }
      } catch (error) {
        message.error("Error loading user profile: " + error.message)
      } finally {
        setLoading(false)
      }
    }

    fetchUserData()
  }, [user])

  // File upload
  const beforeUpload = (file) => {
    const isImage = file.type === "image/jpeg" || file.type === "image/png"
    if (!isImage) {
      message.error("You can only upload JPG/PNG file!")
      return false
    }
    if (file.size / 1024 / 1024 > 2) {
      message.error("Image must be smaller than 2MB!")
      return false
    }
    return true
  }

  const uploadFile = async ({ file }) => {
    setLoading(true)
    try {
      const fileRef = ref(storage, `users/${user.uid}/${file.name}`)
      const snapshot = await uploadBytes(fileRef, file)
      const downloadURL = await getDownloadURL(snapshot.ref)
      setImageUrl(downloadURL)
      message.success("Profile picture uploaded!")
    } catch (err) {
      message.error("Error uploading file: " + err.message)
    } finally {
      setLoading(false)
    }
  }

  // Save profile
  const saveProfile = async () => {
    setLoading(true)
    try {
      // Update Firebase Auth profile
      await updateProfile(auth.currentUser, {
        displayName: name,
        photoURL: imageUrl,
      })

      // Update Firestore user document
      const userRef = doc(db, "users", user.uid)
      await updateDoc(userRef, {
        imageUrl,
        name,
        email,
        phoneNumber,
        slackEmail,
        notifications,
        notificationTime,
        sendDailyOrderRecapMobileNotification: sendDailyOrderRecap,
        sendOrderCompletedMobileNotification: sendOrderCompleted,
        sendOrderDoneByDesignerMobileNotification: sendOrderDoneByDesigner,
        roles,
      })

      message.success("Profile updated successfully!")
    } catch (error) {
      message.error("Error updating profile: " + error.message)
    } finally {
      setLoading(false)
    }
  }

  // Change password
  const changePassword = async () => {
    if (!oldPassword || !newPassword || !confirmNewPassword) {
      message.error("Please fill in all password fields")
      return
    }

    if (newPassword !== confirmNewPassword) {
      message.error("New passwords do not match!")
      return
    }

    setLoading(true)
    try {
      const credential = EmailAuthProvider.credential(user.email, oldPassword)
      await reauthenticateWithCredential(auth.currentUser, credential)
      await updatePassword(auth.currentUser, newPassword)

      message.success("Password changed successfully!")
      setOldPassword("")
      setNewPassword("")
      setConfirmNewPassword("")
    } catch (error) {
      message.error("Error changing password: " + error.message)
    } finally {
      setLoading(false)
    }
  }

  const rolesList = [
    { key: "admin", label: "Admin" },
    { key: "semiAdmin", label: "Semi Admin" },
    { key: "sales", label: "Sales" },
    { key: "accountant", label: "Accountant" },
    { key: "paperManager", label: "Paper Manager" },
    { key: "designer", label: "Designer" },
    { key: "arsomaUser", label: "Arsoma User" },
    { key: "tapeUser", label: "Tape User" },
    { key: "vivaUser", label: "Viva User" },
    { key: "winder", label: "Winder" },
    { key: "warehouse", label: "Warehouse" },
    { key: "courier", label: "Courier" },
    { key: "cutter", label: "Cutter" },
    { key: "devSpecialist", label: "Dev Specialist" },
  ]

  if (loading && !name) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div style={{ padding: "24px", maxWidth: "900px" }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Title level={3} style={{ margin: 0 }}>
            <UserOutlined /> User Profile
          </Title>

          {/* PROFILE PICTURE & BASIC INFO */}
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={6}>
              <div style={{ textAlign: "center" }}>
                <Upload
                  name="avatar"
                  listType="picture-circle"
                  showUploadList={false}
                  customRequest={uploadFile}
                  beforeUpload={beforeUpload}
                  style={{ cursor: "pointer" }}>
                  <div>
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt="avatar"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "50%",
                        }}
                      />
                    ) : (
                      <UserOutlined style={{ fontSize: 48, color: "#bfbfbf" }} />
                    )}
                  </div>
                </Upload>
                <div style={{ marginTop: 12 }}>
                  <Upload
                    name="avatar"
                    showUploadList={false}
                    customRequest={uploadFile}
                    beforeUpload={beforeUpload}>
                    <Button size="small" icon={<UploadOutlined />}>
                      Upload
                    </Button>
                  </Upload>
                </div>
              </div>
            </Col>

            <Col xs={24} sm={18}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Display Name" required style={{ marginBottom: 0 }}>
                    <Input
                      prefix={<UserOutlined />}
                      placeholder="Your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Email (Login)" style={{ marginBottom: 0 }}>
                    <Input prefix={<MailOutlined />} type="email" disabled value={email} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Phone Number" style={{ marginBottom: 0 }}>
                    <Input
                      prefix={<PhoneOutlined />}
                      placeholder="+371 2X XXX XXX"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                  </Form.Item>
                </Col>

              </Row>
            </Col>
          </Row>

          <Divider />

          {/* SLACK EMAIL */}
          <div>
            <Title level={5}>
              <MailOutlined /> Slack Email
            </Title>
            <Form.Item label="Slack Email Address" style={{ marginBottom: 0 }}>
              <Input
                prefix={<MailOutlined />}
                type="email"
                placeholder="slack-email@example.com"
                value={slackEmail}
                onChange={(e) => setSlackEmail(e.target.value)}
              />
            </Form.Item>
            <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
              Used for Slack bot notifications
            </Text>
          </div>

          <Divider />

          {/* NOTIFICATION PREFERENCES */}
          <div>
            <Title level={5}>
              <BellOutlined /> Notifications
            </Title>
            <Row gutter={[24, 16]}>
              <Col xs={24}>
                <Checkbox
                  checked={notifications}
                  onChange={(e) => setNotifications(e.target.checked)}>
                  <span style={{ marginLeft: 8 }}>Enable General Notifications</span>
                </Checkbox>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="Notification Time" style={{ marginBottom: 0 }}>
                  <Input
                    type="time"
                    value={notificationTime}
                    onChange={(e) => setNotificationTime(e.target.value)}
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Text strong style={{ display: "block", marginBottom: 12 }}>
                  Mobile Notifications:
                </Text>
                <Space direction="vertical">
                  <Checkbox
                    checked={sendDailyOrderRecap}
                    onChange={(e) => setSendDailyOrderRecap(e.target.checked)}>
                    <span style={{ marginLeft: 8 }}>Daily Order Recap</span>
                  </Checkbox>
                  <Checkbox
                    checked={sendOrderCompleted}
                    onChange={(e) => setSendOrderCompleted(e.target.checked)}>
                    <span style={{ marginLeft: 8 }}>Order Completed</span>
                  </Checkbox>
                  <Checkbox
                    checked={sendOrderDoneByDesigner}
                    onChange={(e) => setSendOrderDoneByDesigner(e.target.checked)}>
                    <span style={{ marginLeft: 8 }}>Order Done By Designer</span>
                  </Checkbox>
                </Space>
              </Col>
            </Row>
          </div>

          <Divider />

          {/* ROLES */}
          <div>
            <Title level={5}>
              <TeamOutlined /> Roles
            </Title>
            <Space wrap>
              {Object.entries(roles)
                .filter(([_, value]) => value)
                .map(([key, _]) => (
                  <Tag key={key} color="blue">
                    {rolesList.find((r) => r.key === key)?.label}
                  </Tag>
                ))}
            </Space>
            {Object.values(roles).every((v) => !v) && (
              <Text type="secondary">No roles assigned</Text>
            )}
          </div>

          <Divider />

          {/* MOBILE APP INFO */}
          {(hasAppLogin || mobileAppLastLogin || expoPushToken) && (
            <>
              <div>
                <Title level={5}>
                  <MobileOutlined /> Mobile App
                </Title>
                <Row gutter={[16, 16]}>
                  {hasAppLogin && (
                    <Col xs={24}>
                      <Text>
                        <strong>App Login:</strong> <Tag color="green">Enabled</Tag>
                      </Text>
                    </Col>
                  )}
                  {mobileAppLastLogin && (
                    <Col xs={24}>
                      <Text>
                        <strong>Last Login:</strong> {mobileAppLastLogin}
                      </Text>
                    </Col>
                  )}
                  {expoPushToken && (
                    <Col xs={24}>
                      <Text strong style={{ display: "block", marginBottom: 8 }}>
                        Expo Token:
                      </Text>
                      <div
                        style={{
                          padding: "8px 12px",
                          background: "#f0f0f0",
                          borderRadius: "4px",
                          wordBreak: "break-all",
                          fontSize: "12px",
                          fontFamily: "monospace",
                        }}>
                        {expoPushToken}
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
              <Divider />
            </>
          )}

          {/* PASSWORD CHANGE */}
          <div>
            <Title level={5}>
              <LockOutlined /> Change Password
            </Title>
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              <div>
                <Text strong style={{ display: "block", marginBottom: 12 }}>
                  Verify Current Password
                </Text>
                <Form.Item label="Current Password" style={{ marginBottom: 0 }}>
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Enter your current password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                  />
                </Form.Item>
              </div>

              <Divider style={{ margin: "12px 0" }} />

              <div>
                <Text strong style={{ display: "block", marginBottom: 12 }}>
                  Set New Password
                </Text>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Form.Item label="New Password" style={{ marginBottom: 0 }}>
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </Form.Item>

                  <Form.Item label="Confirm New Password" style={{ marginBottom: 0 }}>
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Confirm new password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                    />
                  </Form.Item>
                </Space>
              </div>

              <Button
                type="primary"
                danger
                icon={<LockOutlined />}
                onClick={changePassword}
                loading={loading}
                style={{ width: "100%" }}>
                Update Password
              </Button>
            </Space>
          </div>

          <Divider />

          {/* SAVE BUTTON */}
          <Row justify="end">
            <Space>
              <Button size="large">Cancel</Button>
              <Button
                type="primary"
                size="large"
                icon={<SaveOutlined />}
                onClick={saveProfile}
                loading={loading}>
                Save All Changes
              </Button>
            </Space>
          </Row>
        </Space>
      </Card>
    </div>
  )
}
